import { AssetsProvider, useQuery } from '@obvio/app'
import { serverClient } from '@obvio/server'

import { schema } from '@/api/schema'
import { Layout } from '@/components/Layout'
import { PageSeo } from '@/components/PageSeo'
import { ProfitroomOfferPage } from '@/components/ProfitroomOfferPage'
import { QUERIES } from '@/constants/queries'
import { REVALIDATE_SECONDS } from '@/constants/revalidate'
import { describeRichText, withSiteName } from '@/utils/seo'

import type { Schema } from '@obvio/app'
import type { DeepPick } from '@obvio/utils'
import type {
  GetStaticPathsResult,
  GetStaticPropsContext,
  GetStaticPropsResult,
} from 'next'
import type { ReactElement } from 'react'

const OFFER_SELECT = {
  id: true,
  image: true,
  title: true,
  slug: true,
  description: true,
  seoTitle: true,
  seoDescription: true,
  image1: true,
  image2: true,
  text1: true,
  text2: true,
  availableFrom: true,
  availableTo: true,
  pricePer: true,
  startingPrice: true,
  profitroomId: true,
} as const

export type Data = DeepPick<Schema['ProfitroomOffer'], typeof OFFER_SELECT>

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  const client = await serverClient(schema, { isAdmin: true })

  const offers = await client.query('getProfitroomOffers', {
    select: {
      slug: true,
    },
  })

  const paths = offers.map(({ slug }) => ({
    params: { slug },
  }))

  return {
    paths,
    fallback: false,
  }
}

export async function getStaticProps(
  ctx: GetStaticPropsContext<{ slug: string }>,
): Promise<GetStaticPropsResult<{ slug: string }>> {
  const client = await serverClient(schema, { ...ctx, isAdmin: true })

  const slug = ctx?.params?.slug

  if (!slug) {
    return { notFound: true }
  }

  const offer = await client.query('getProfitroomOffer', {
    args: { slug },
    select: OFFER_SELECT,
  })

  await client.query('getProfitroomOffers', {
    select: QUERIES['packages-carousel'].getProfitroomOffers,
  })

  if (!offer) {
    return { notFound: true }
  }

  return {
    ...client.ssrData({ slug }),
    revalidate: REVALIDATE_SECONDS,
  }
}

type OfferProps = {
  slug: string
}

function Category({ slug }: OfferProps): ReactElement | null {
  const { data: offer } = useQuery('getProfitroomOffer', {
    args: { slug },
    select: OFFER_SELECT,
  })

  if (!offer) {
    return null
  }

  return (
    <Layout type="Główny">
      <PageSeo
        path={`pakiety/${offer.slug}`}
        title={offer.seoTitle || withSiteName(`Pakiet ${offer.title}`)}
        description={
          offer.seoDescription || describeRichText(offer.description)
        }
      />
      <AssetsProvider key={offer.id} path={`/offer/${offer.id}`}>
        <ProfitroomOfferPage offer={offer} />
      </AssetsProvider>
    </Layout>
  )
}

export default Category
